/**
 * useDashboardPrefs — Persists dashboard sort & collapse preferences.
 *
 * Reads from localStorage on mount (instant), syncs to Firestore
 * user.settings.dashboardPrefs for cross-device persistence.
 *
 * Shape:
 *   {
 *     sections: {
 *       "Expected This Week": { sortField: "name"|"date", sortDirection: "asc"|"desc", collapsed: false },
 *       "Recently Seen":      { sortField: "date", sortDirection: "desc", collapsed: false },
 *       "Pinned Patients":    { sortField: "name", sortDirection: "asc", collapsed: false },
 *     }
 *   }
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { getUserSettings, updateUserSettings } from "../utils/firestoreService";
import { useAuth } from "../utils/authContext";

const LOCAL_STORAGE_KEY = "dashboardPrefs";

const DEFAULT_SECTION_PREFS = {
    sortField: "name",
    sortDirection: "asc",
    collapsed: false,
};

function loadFromLocalStorage() {
    try {
        const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

function saveToLocalStorage(prefs) {
    try {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(prefs));
    } catch {
        // localStorage full or disabled — ignore
    }
}

export function useDashboardPrefs() {
    const { currentUser } = useAuth();
    const userId = currentUser?.uid;
    const [prefs, setPrefs] = useState(() => loadFromLocalStorage() || { sections: {} });
    const saveTimerRef = useRef(null);

    // Load from Firestore on mount (merge with localStorage)
    useEffect(() => {
        if (!userId) return;
        let cancelled = false;

        (async () => {
            try {
                const settings = await getUserSettings(userId);
                const remote = settings?.dashboardPrefs;
                if (remote && !cancelled) {
                    setPrefs((prev) => {
                        const merged = { sections: { ...remote.sections, ...prev.sections } };
                        saveToLocalStorage(merged);
                        return merged;
                    });
                }
            } catch {
                // Firestore unavailable — rely on localStorage
            }
        })();

        return () => { cancelled = true; };
    }, [userId]);

    // Debounced Firestore save
    const persistToFirestore = useCallback(
        (newPrefs) => {
            if (!userId) return;
            if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
            saveTimerRef.current = setTimeout(() => {
                updateUserSettings(userId, { dashboardPrefs: newPrefs }).catch(() => {});
            }, 1000);
        },
        [userId]
    );

    const getSectionPrefs = useCallback(
        (sectionTitle) => {
            return { ...DEFAULT_SECTION_PREFS, ...prefs.sections?.[sectionTitle] };
        },
        [prefs]
    );

    const setSectionSort = useCallback(
        (sectionTitle, sortField, sortDirection) => {
            setPrefs((prev) => {
                const updated = {
                    ...prev,
                    sections: {
                        ...prev.sections,
                        [sectionTitle]: {
                            ...DEFAULT_SECTION_PREFS,
                            ...prev.sections?.[sectionTitle],
                            sortField,
                            sortDirection,
                        },
                    },
                };
                saveToLocalStorage(updated);
                persistToFirestore(updated);
                return updated;
            });
        },
        [persistToFirestore]
    );

    const setSectionCollapsed = useCallback(
        (sectionTitle, collapsed) => {
            setPrefs((prev) => {
                const updated = {
                    ...prev,
                    sections: {
                        ...prev.sections,
                        [sectionTitle]: {
                            ...DEFAULT_SECTION_PREFS,
                            ...prev.sections?.[sectionTitle],
                            collapsed,
                        },
                    },
                };
                saveToLocalStorage(updated);
                persistToFirestore(updated);
                return updated;
            });
        },
        [persistToFirestore]
    );

    return { getSectionPrefs, setSectionSort, setSectionCollapsed };
}
